import React, {useCallback, useState} from 'react';
import {Button, styled} from '@mui/material';
import {useAppProps, useModals} from '../../App';
import {VotePageProps} from '../../tools/front-props';
import {VotedWatchesDialog} from '../voted-watches-dialog';

const Root = styled('div')`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

export const WatchesGrid = styled('div')`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(calc(min(250px, 100%)), 1fr));
    margin-top: 20px;
    grid-gap: 20px;
`;

export const WatchBlock = styled('div')`
    display: flex;
    flex-direction: column;
    align-items: start;
    position: relative;
`;

export const WatchImg = styled('div')`
    position: relative;
    width: 100%;
    height: 0;
    padding-bottom: 100%;
    border: 1px solid black;

    a {
        position: absolute;
        top: 50%;
        margin-bottom: auto;
        bottom: -50%;
        width: 100%;
        transform: translateY(-50%);

        img {
            object-fit: cover;
            width: 100%;
            height: 100%;
        }
    }
`;

export const VoteCategoryRoot = styled('div')`
    width: 100%;
    margin-top: 20px;
`;

export const CategoryTop = styled('div')<{ burl: string }>`
    background-image: url(${props => props.burl});
    background-repeat: no-repeat;
    text-transform: uppercase;
    color: black;
    border-bottom: 1px solid #aaaaaa;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    height: 80px;
    position: relative;
`;

export const CategoryTopMobile = styled(CategoryTop)`
    display: none;
    @media (max-width: 1200px) {
        display: flex;
    }
`;

export const CategoryTopDesktop = styled(CategoryTop)`
    display: flex;
    @media (max-width: 1200px) {
        display: none;
    }
`;

export const CategoryTitle = styled('h3')`
    margin: 0 20px;
    color: #fff;
    font-size: 1.5em;
    flex: 1 0 80px;
`;

export const CategoryBannerContainerDesktop = styled('div')`
    flex: 0 0 614px;
    margin: 0 10px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: end;
`;

export const CategoryBannerContainerMobile = styled('div')`
    border: 1px solid black;
    height: 60px;
    width: 614px;
    max-width: 100%;
    align-items: center;
    justify-content: end;
    display: none;
    margin: auto;
    @media (max-width: 1200px) {
        display: flex;
    }
`;

export const CategoryBanner = styled('img')`
    object-fit: contain;
    height: 100%;
    width: 100%;
`;

export const WatchBrand = styled('h4')`
    font-family: NotoSans, serif;
    text-decoration: none;
    color: black !important;
    margin: 10px 0 5px;
`;

export const WatchName = styled('span')`
    font-family: NotoSans, serif;
    color: gray;
    margin-bottom: 10px;
`;

export const NeedEmailNotification = styled('span')`
    font-size: 16px;
    text-align: start;
    width: 100%;
    margin-bottom: 10px;
`;

const NeedEmailNotificationA = styled('span')`
    cursor: pointer;
    text-decoration: underline;
    color: mediumblue;
`;

const NominationsContainer = styled('div')`
    display: flex;
    flex-direction: row;
    align-items: start;
    justify-content: space-between;
    padding-top: 20px;
`;

const NominationsList = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const NominationLink = styled('a')`
    text-decoration: none;
    color: #6F6F6E;
    font-weight: bold;
    font-size: 18px;
    text-transform: uppercase;
`;

const VoteInfoText = styled('p')`
    overflow: hidden;
    margin: 0;
    font-size: 16px;
    flex: 0 0 50%;
`;


export const VotePage: React.FC = () => {
    const {pageData, email, siteData} = useAppProps();
    const {authEnabled, voteBackground} = siteData;
    const {setAuthModalOpen} = useModals();
    const {voteCategories, votePage} = pageData as VotePageProps;
    const [votedIds] = useState([] as string[]);
    const [votedWatchesDialogWatchId, setVotedWatchesDialogWatchId] = useState<string | undefined>(undefined);

    const logout = useCallback(
        async () => {
            const form = document.createElement('form');
            document.body.appendChild(form);
            form.action = '/auth/logout';
            form.method = 'post';
            form.submit();
        },
        [],
    );

    const tryToVote = useCallback(
        (watchId: string) => {
            if (email === '') {
                setAuthModalOpen(true);
                return;
            }
            setVotedWatchesDialogWatchId(watchId);
        },
        [email, setAuthModalOpen],
    );

    return <Root>
        <VotedWatchesDialog watchIdToVote={votedWatchesDialogWatchId} onClose={() => {
            setVotedWatchesDialogWatchId(undefined);
        }}/>
        {authEnabled && email === '' && <NeedEmailNotification>
            Чтобы проголосовать, <NeedEmailNotificationA onClick={() => {
            setAuthModalOpen(true);
        }}>укажите свою
            почту</NeedEmailNotificationA>
        </NeedEmailNotification>}
        {email !== '' && <NeedEmailNotification>
            Вы вошли как <b>{email}</b>. <NeedEmailNotificationA onClick={logout}> Выйти</NeedEmailNotificationA>
        </NeedEmailNotification>}
        <NominationsContainer>
            <NominationsList>
                {voteCategories.map(voteCategory => {
                    return <NominationLink href={`#${voteCategory.title}`} key={voteCategory.title}>
                        {voteCategory.title}
                    </NominationLink>;
                })}
            </NominationsList>
            <VoteInfoText dangerouslySetInnerHTML={{__html: votePage.topNote}}/>
        </NominationsContainer>
        {voteCategories.map(voteCategory => <VoteCategoryRoot key={voteCategory.title}>
            <CategoryTopDesktop burl={voteBackground}>
                <CategoryTitle id={voteCategory.title}>
                    {voteCategory.title}
                </CategoryTitle>
                <CategoryBannerContainerDesktop>
                    <CategoryBanner src={voteCategory.banner.image}/>
                </CategoryBannerContainerDesktop>
            </CategoryTopDesktop>
            <CategoryTopMobile burl={voteBackground}>
                <CategoryTitle id={voteCategory.title}>
                    {voteCategory.title}
                </CategoryTitle>
            </CategoryTopMobile>
            <CategoryBannerContainerMobile>
                <CategoryBanner src={voteCategory.banner.image}/>
            </CategoryBannerContainerMobile>
            <WatchesGrid>
                {voteCategory.watches.map(watch => {
                    const voted = votedIds.includes(watch.id);
                    return <WatchBlock key={watch.id}>
                        <WatchImg>
                            <a href={`/watch/${watch.id}`}>
                                <img
                                    src={`${watch.images[0]}?width=500`}
                                    srcSet={`
                                        ${watch.images[0]}?width=250 250w,
                                        ${watch.images[0]}?width=500 500w,
                                        ${watch.images[0]}?width=750 750w,
                                        ${watch.images[0]}?width=1000 1000w
                                    `}
                                    sizes="(max-width: 600px) 100vw, (max-width: 1200px) 50vw,33vw"
                                    alt={watch.name}
                                    style={{width: '100%', height: 'auto'}}
                                />
                            </a>
                        </WatchImg>
                        <a href={`/watch/${watch.id}`}>
                            <WatchBrand>
                                {watch.brand}
                            </WatchBrand>
                        </a>
                        <WatchName>
                            {watch.name}
                        </WatchName>
                        {siteData.voteActive &&
                            <Button variant={'contained'} onClick={() => tryToVote(watch.id)}
                                    disabled={voted || !watch.wotable}>
                                {watch.wotable ? (voted ? 'Выбрано!' : 'Проголосовать') : 'Недоступно'}
                            </Button>
                        }
                    </WatchBlock>;
                })}
            </WatchesGrid>
        </VoteCategoryRoot>)}
    </Root>;
};